/**
 * Enterprise Audit Trail System
 * ==============================
 * Comprehensive audit logging UI with:
 * - Advanced filtering
 * - Full-text search
 * - Server-side pagination
 * - Detail panel
 * - Export functionality
 * - Smart filter chips
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatRelativeTimeIST } from "@/lib/time";
import { toast } from "sonner";

// UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

// Icons
import {
  Search, Filter, Download, RefreshCw, ChevronLeft, ChevronRight,
  Clock, User, Store, Package, ShoppingCart, Truck, Users, CreditCard,
  DollarSign, Settings, FileText, Shield, AlertTriangle, CheckCircle2,
  XCircle, Info, ArrowRight, Eye, X, Calendar, Activity, Database,
  History, Layers, Tag, Hash, Globe, Monitor, Smartphone,
} from "lucide-react";

// ============================================================================
// Constants
// ============================================================================

const MODULE_ICONS = {
  auth: Shield,
  user: User,
  shop: Store,
  product: Package,
  stock: Database,
  sale: ShoppingCart,
  sale_return: ShoppingCart,
  transfer: Truck,
  purchase: FileText,
  purchase_order: FileText,
  customer: Users,
  customer_ledger: CreditCard,
  supplier: Users,
  supplier_ledger: CreditCard,
  expense: DollarSign,
  income: DollarSign,
  cash_register: DollarSign,
  requirement: FileText,
  restock: Package,
  settings: Settings,
  import: FileText,
  export: FileText,
  print: FileText,
  system: Settings,
};

const MODULE_COLORS = {
  auth: "bg-purple-100 text-purple-700 border-purple-200",
  user: "bg-blue-100 text-blue-700 border-blue-200",
  shop: "bg-amber-100 text-amber-700 border-amber-200",
  product: "bg-green-100 text-green-700 border-green-200",
  stock: "bg-cyan-100 text-cyan-700 border-cyan-200",
  sale: "bg-emerald-100 text-emerald-700 border-emerald-200",
  sale_return: "bg-orange-100 text-orange-700 border-orange-200",
  transfer: "bg-indigo-100 text-indigo-700 border-indigo-200",
  purchase: "bg-violet-100 text-violet-700 border-violet-200",
  customer: "bg-pink-100 text-pink-700 border-pink-200",
  supplier: "bg-rose-100 text-rose-700 border-rose-200",
  expense: "bg-red-100 text-red-700 border-red-200",
  income: "bg-green-100 text-green-700 border-green-200",
  cash_register: "bg-yellow-100 text-yellow-700 border-yellow-200",
  system: "bg-gray-100 text-gray-700 border-gray-200",
};

const SEVERITY_CONFIG = {
  info: { icon: Info, color: "bg-blue-50 text-blue-700 border-blue-200" },
  success: { icon: CheckCircle2, color: "bg-green-50 text-green-700 border-green-200" },
  warning: { icon: AlertTriangle, color: "bg-amber-50 text-amber-700 border-amber-200" },
  error: { icon: XCircle, color: "bg-red-50 text-red-700 border-red-200" },
  critical: { icon: AlertTriangle, color: "bg-red-100 text-red-800 border-red-300" },
};

const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "24h", label: "Last 24 Hours" },
  { value: "48h", label: "Last 48 Hours" },
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "90d", label: "Last 90 Days" },
  { value: "year", label: "This Year" },
];

const SMART_FILTERS = [
  { key: "my_activity", label: "My Activity", icon: User },
  { key: "login_failures", label: "Login Failures", icon: Shield },
  { key: "deletions", label: "Deletions", icon: XCircle },
  { key: "changes", label: "Changes Only", icon: History },
  { key: "sales", label: "Sales", icon: ShoppingCart },
  { key: "stock", label: "Stock Changes", icon: Package },
  { key: "transfers", label: "Transfers", icon: Truck },
  { key: "customers", label: "Customers", icon: Users },
];

// ============================================================================
// Utility Functions
// ============================================================================

function formatTimestamp(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatRelativeTime(ts) {
  if (!ts) return "";
  const now = new Date();
  const then = new Date(ts);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  
  // For very recent events (within last 24 hours), use minute/hour granularity
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  
  // For older events, use calendar-day based relative time (IST)
  return formatRelativeTimeIST(ts);
}

// ============================================================================
// Components
// ============================================================================

function ModuleBadge({ module }) {
  const Icon = MODULE_ICONS[module] || Activity;
  const colorClass = MODULE_COLORS[module] || MODULE_COLORS.system;
  
  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0.5 ${colorClass}`}>
      <Icon className="w-3 h-3 mr-1" />
      {module?.replace("_", " ").toUpperCase()}
    </Badge>
  );
}

function SeverityBadge({ severity }) {
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0.5 ${config.color}`}>
      <Icon className="w-3 h-3 mr-0.5" />
      {severity?.toUpperCase()}
    </Badge>
  );
}

function ActionBadge({ action }) {
  return (
    <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 font-mono">
      {action?.replace("_", " ")}
    </Badge>
  );
}

function FieldChange({ change }) {
  return (
    <div className="flex items-center gap-2 text-xs py-1 border-b border-dashed last:border-0">
      <span className="font-medium text-muted-foreground w-24 truncate" title={change.field}>
        {change.field?.replace("_", " ")}
      </span>
      <span className="text-red-600 line-through truncate max-w-[100px]" title={String(change.old_value)}>
        {change.old_value ?? "—"}
      </span>
      <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
      <span className="text-green-600 font-medium truncate max-w-[100px]" title={String(change.new_value)}>
        {change.new_value ?? "—"}
      </span>
    </div>
  );
}

function EventRow({ event, onClick, isSelected }) {
  const Icon = MODULE_ICONS[event.module] || Activity;
  
  return (
    <div
      onClick={() => onClick(event)}
      className={`
        flex items-start gap-3 px-3 py-2.5 border-b cursor-pointer transition-colors
        hover:bg-muted/50
        ${isSelected ? "bg-primary/5 border-l-2 border-l-primary" : ""}
      `}
      data-testid={`audit-event-${event.id}`}
    >
      {/* Icon */}
      <div className={`p-1.5 rounded ${MODULE_COLORS[event.module] || "bg-gray-100"}`}>
        <Icon className="w-4 h-4" />
      </div>
      
      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Top row: Summary + Time */}
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium truncate" title={event.summary}>
            {event.summary || `${event.action} ${event.entity_type}`}
          </p>
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
            {formatRelativeTime(event.timestamp)}
          </span>
        </div>
        
        {/* Middle row: Badges */}
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <ActionBadge action={event.action} />
          {event.severity && event.severity !== "info" && (
            <SeverityBadge severity={event.severity} />
          )}
          {event.changes_count > 0 && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {event.changes_count} changes
            </Badge>
          )}
        </div>
        
        {/* Bottom row: Meta */}
        <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
          {event.actor_name && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {event.actor_name}
            </span>
          )}
          {event.shop_name && (
            <span className="flex items-center gap-1">
              <Store className="w-3 h-3" />
              {event.shop_name}
            </span>
          )}
          {event.entity_name && (
            <span className="truncate max-w-[150px]" title={event.entity_name}>
              {event.entity_type}: {event.entity_name}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function EventDetailPanel({ event, onClose, onViewRelated }) {
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  
  useEffect(() => {
    if (event?.transaction_id) {
      setLoadingRelated(true);
      api.get(`/audit-trail/events/${event.id}`)
        .then(({ data }) => {
          setRelatedEvents(data.related_events || []);
        })
        .catch(() => {})
        .finally(() => setLoadingRelated(false));
    }
  }, [event?.id, event?.transaction_id]);
  
  if (!event) return null;
  
  return (
    <Sheet open={!!event} onOpenChange={() => onClose()}>
      <SheetContent className="w-[500px] sm:max-w-[500px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ModuleBadge module={event.module} />
            <span className="truncate">{event.action?.replace("_", " ")}</span>
          </SheetTitle>
        </SheetHeader>
        
        <div className="mt-4 space-y-4">
          {/* Summary */}
          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-sm">{event.summary}</p>
            <p className="text-[10px] text-muted-foreground mt-1">
              {formatTimestamp(event.timestamp)}
            </p>
          </div>
          
          {/* Classification */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="p-2 bg-muted/20 rounded">
              <Label className="text-[10px] text-muted-foreground">Severity</Label>
              <div className="mt-1"><SeverityBadge severity={event.severity} /></div>
            </div>
            <div className="p-2 bg-muted/20 rounded">
              <Label className="text-[10px] text-muted-foreground">Result</Label>
              <div className="mt-1">
                <Badge variant={event.result === "success" ? "default" : "destructive"} className="text-[10px]">
                  {event.result}
                </Badge>
              </div>
            </div>
          </div>
          
          {/* Actor */}
          {event.actor_name && (
            <div className="p-3 border rounded-lg">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Actor</Label>
              <div className="flex items-center gap-2 mt-1">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{event.actor_name}</span>
                {event.actor_role && (
                  <Badge variant="outline" className="text-[9px]">{event.actor_role}</Badge>
                )}
              </div>
            </div>
          )}
          
          {/* Entity */}
          <div className="p-3 border rounded-lg">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Entity</Label>
            <div className="mt-1 text-sm">
              <span className="text-muted-foreground">{event.entity_type}: </span>
              <span className="font-medium">{event.entity_name || event.entity_id}</span>
            </div>
          </div>
          
          {/* Field Changes */}
          {event.field_changes?.length > 0 && (
            <div className="p-3 border rounded-lg">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">
                Field Changes ({event.field_changes.length})
              </Label>
              <div className="mt-2">
                {event.field_changes.map((change, idx) => (
                  <FieldChange key={idx} change={change} />
                ))}
              </div>
            </div>
          )}
          
          {/* Related IDs */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            {event.shop_name && (
              <div className="p-2 bg-muted/20 rounded">
                <Label className="text-[10px] text-muted-foreground">Shop</Label>
                <p className="mt-0.5 font-medium truncate">{event.shop_name}</p>
              </div>
            )}
            {event.customer_name && (
              <div className="p-2 bg-muted/20 rounded">
                <Label className="text-[10px] text-muted-foreground">Customer</Label>
                <p className="mt-0.5 font-medium truncate">{event.customer_name}</p>
              </div>
            )}
            {event.product_name && (
              <div className="p-2 bg-muted/20 rounded">
                <Label className="text-[10px] text-muted-foreground">Product</Label>
                <p className="mt-0.5 font-medium truncate">{event.product_name}</p>
              </div>
            )}
            {event.sale_number && (
              <div className="p-2 bg-muted/20 rounded">
                <Label className="text-[10px] text-muted-foreground">Sale #</Label>
                <p className="mt-0.5 font-mono">{event.sale_number}</p>
              </div>
            )}
          </div>
          
          {/* Related Events */}
          {relatedEvents.length > 0 && (
            <div className="p-3 border rounded-lg">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">
                Related Events ({relatedEvents.length})
              </Label>
              <div className="mt-2 space-y-1">
                {relatedEvents.map((rel) => (
                  <div
                    key={rel.id}
                    className="flex items-center gap-2 text-xs p-1.5 bg-muted/20 rounded cursor-pointer hover:bg-muted/40"
                    onClick={() => onViewRelated(rel)}
                  >
                    <ActionBadge action={rel.action} />
                    <span className="truncate flex-1">{rel.summary}</span>
                    <span className="text-muted-foreground text-[10px]">
                      {formatRelativeTime(rel.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Technical Details */}
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Technical Details
            </summary>
            <div className="mt-2 p-2 bg-muted/20 rounded font-mono text-[10px] space-y-1">
              <p>Event ID: {event.id}</p>
              <p>Transaction: {event.transaction_id}</p>
              {event.ip_address && <p>IP: {event.ip_address}</p>}
              {event.request_id && <p>Request: {event.request_id}</p>}
            </div>
          </details>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function FilterChip({ label, onRemove, icon: Icon }) {
  return (
    <Badge variant="secondary" className="pl-2 pr-1 py-1 gap-1">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
      <button onClick={onRemove} className="ml-1 hover:bg-muted rounded p-0.5">
        <X className="w-3 h-3" />
      </button>
    </Badge>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function AuditTrailPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [datePreset, setDatePreset] = useState(searchParams.get("date") || "7d");
  const [selectedModule, setSelectedModule] = useState(searchParams.get("module") || "");
  const [selectedAction, setSelectedAction] = useState(searchParams.get("action") || "");
  const [selectedSeverity, setSelectedSeverity] = useState(searchParams.get("severity") || "");
  const [selectedActor, setSelectedActor] = useState(searchParams.get("actor") || "");
  const [changesOnly, setChangesOnly] = useState(false);
  const [deletionsOnly, setDeletionsOnly] = useState(false);
  
  // Facets
  const [facets, setFacets] = useState({ modules: [], actions: [], actors: [], severities: [] });
  
  // Detail panel
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Fetch facets on mount
  useEffect(() => {
    api.get("/audit-trail/facets")
      .then(({ data }) => setFacets(data))
      .catch(() => {});
  }, []);
  
  // Fetch stats
  useEffect(() => {
    api.get("/audit-trail/stats", { params: { date_preset: datePreset } })
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, [datePreset]);
  
  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        sort_order: "desc",
        date_preset: datePreset,
      };
      
      if (search) params.search = search;
      if (selectedModule) params.module = selectedModule;
      if (selectedAction) params.action = selectedAction;
      if (selectedSeverity) params.severity = selectedSeverity;
      if (selectedActor) params.actor_id = selectedActor;
      if (changesOnly) params.changes_only = true;
      if (deletionsOnly) params.deletions_only = true;
      
      const { data } = await api.get("/audit-trail/events", { params });
      setEvents(data.events || []);
      setTotalCount(data.total || 0);
      setTotalPages(data.total_pages || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, datePreset, selectedModule, selectedAction, selectedSeverity, selectedActor, changesOnly, deletionsOnly]);
  
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  
  // Export handler
  const handleExport = async (format) => {
    try {
      const params = { format, date_preset: datePreset };
      if (search) params.search = search;
      if (selectedModule) params.module = selectedModule;
      
      const response = await api.get("/audit-trail/export", { 
        params, 
        responseType: "blob" 
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `audit_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  
  // Clear all filters
  const clearFilters = () => {
    setSearch("");
    setSelectedModule("");
    setSelectedAction("");
    setSelectedSeverity("");
    setSelectedActor("");
    setChangesOnly(false);
    setDeletionsOnly(false);
    setDatePreset("7d");
    setPage(1);
  };
  
  // Active filter count
  const activeFilterCount = [
    search, selectedModule, selectedAction, selectedSeverity, selectedActor,
    changesOnly, deletionsOnly
  ].filter(Boolean).length;
  
  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="audit-trail-page">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-background">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <History className="w-5 h-5" />
              Audit Trail
            </h1>
            <p className="text-xs text-muted-foreground">
              {totalCount.toLocaleString()} events · Enterprise activity log
            </p>
          </div>
          
          {/* Quick Stats */}
          {stats && (
            <div className="hidden md:flex items-center gap-4 text-xs">
              <div className="text-center">
                <p className="font-mono text-lg font-bold">{stats.total_events?.toLocaleString()}</p>
                <p className="text-muted-foreground">Total</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-lg font-bold text-amber-600">{stats.changes?.toLocaleString()}</p>
                <p className="text-muted-foreground">Changes</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-lg font-bold text-red-600">{stats.failures?.toLocaleString()}</p>
                <p className="text-muted-foreground">Failures</p>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-1" /> Export
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-40 p-1">
                <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => handleExport("csv")}>
                  CSV
                </Button>
                <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => handleExport("json")}>
                  JSON
                </Button>
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="sm" onClick={fetchEvents}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        
        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events, users, products..."
              className="pl-9 h-9"
              data-testid="audit-search"
            />
          </div>
          
          {/* Date Preset */}
          <Select value={datePreset} onValueChange={(v) => { setDatePreset(v); setPage(1); }}>
            <SelectTrigger className="w-[140px] h-9">
              <Calendar className="w-4 h-4 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Module Filter */}
          <Select value={selectedModule} onValueChange={(v) => { setSelectedModule(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[130px] h-9">
              <Layers className="w-4 h-4 mr-1" />
              <SelectValue placeholder="Module" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modules</SelectItem>
              {facets.modules.map((m) => (
                <SelectItem key={m} value={m}>{m?.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Action Filter */}
          <Select value={selectedAction} onValueChange={(v) => { setSelectedAction(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[130px] h-9">
              <Activity className="w-4 h-4 mr-1" />
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {facets.actions.slice(0, 20).map((a) => (
                <SelectItem key={a} value={a}>{a?.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Severity Filter */}
          <Select value={selectedSeverity} onValueChange={(v) => { setSelectedSeverity(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[120px] h-9">
              <AlertTriangle className="w-4 h-4 mr-1" />
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Special Filters */}
          <div className="flex items-center gap-2 px-2">
            <Checkbox
              id="changes-only"
              checked={changesOnly}
              onCheckedChange={(v) => { setChangesOnly(v); setPage(1); }}
            />
            <Label htmlFor="changes-only" className="text-xs cursor-pointer">Changes</Label>
          </div>
          <div className="flex items-center gap-2 px-2">
            <Checkbox
              id="deletions-only"
              checked={deletionsOnly}
              onCheckedChange={(v) => { setDeletionsOnly(v); setPage(1); }}
            />
            <Label htmlFor="deletions-only" className="text-xs cursor-pointer">Deletions</Label>
          </div>
          
          {/* Clear Filters */}
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="w-4 h-4 mr-1" /> Clear ({activeFilterCount})
            </Button>
          )}
        </div>
        
        {/* Active Filters */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {search && <FilterChip label={`"${search}"`} onRemove={() => setSearch("")} icon={Search} />}
            {selectedModule && <FilterChip label={selectedModule} onRemove={() => setSelectedModule("")} icon={Layers} />}
            {selectedAction && <FilterChip label={selectedAction} onRemove={() => setSelectedAction("")} icon={Activity} />}
            {selectedSeverity && <FilterChip label={selectedSeverity} onRemove={() => setSelectedSeverity("")} icon={AlertTriangle} />}
            {changesOnly && <FilterChip label="Changes Only" onRemove={() => setChangesOnly(false)} icon={History} />}
            {deletionsOnly && <FilterChip label="Deletions Only" onRemove={() => setDeletionsOnly(false)} icon={XCircle} />}
          </div>
        )}
      </div>
      
      {/* Event List */}
      <div className="flex-1 overflow-hidden flex">
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 space-y-2">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <History className="w-12 h-12 mb-4 opacity-20" />
              <p>No events found</p>
              <p className="text-xs">Try adjusting your filters</p>
            </div>
          ) : (
            <div>
              {events.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  onClick={setSelectedEvent}
                  isSelected={selectedEvent?.id === event.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-2 border-t bg-background flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Page {page} of {totalPages} · {totalCount.toLocaleString()} events
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Detail Panel */}
      <EventDetailPanel
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onViewRelated={(rel) => setSelectedEvent(rel)}
      />
    </div>
  );
}
